import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { AvatarLettersPipe } from 'app/pipe/avatar-letters.pipe';

@Component({
    selector: 'avatar',
    imports: [AvatarLettersPipe, NgClass, RouterLink, MatTooltipModule],
    template: `<article
        class="h-10 w-10"
        [ngClass]="{ 'cursor-pointer': link() }"
        [routerLink]="link() || null"
        [ngClass]="containerClass()"
        [matTooltip]="tooltip()"
        [matTooltipDisabled]="!tooltip()"
    >
        @if (src() && !loading()) {
            <img
                [src]="src()"
                alt="user-avatar"
                class="h-full w-full rounded-full object-cover"
                [ngClass]="elementClass()"
            />
        } @else {
            <div
                class="grid h-full w-full select-none place-items-center rounded-full font-medium"
                [class]="avatarColors().bg + ' ' + avatarColors().text"
                [ngClass]="elementClass()"
            >
                {{ name() | avatarLetters }}
            </div>
        }
    </article>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent {
    src = input<string | undefined | null>('');
    name = input<string>('');
    link = input<string>(null);
    loading = input<boolean>(false);
    containerClass = input<string>('');
    elementClass = input<string>('');
    tooltip = input<string>('');

    private colorPairs = [
        { bg: 'bg-blue-800', text: 'text-blue-200' },
        { bg: 'bg-green-800', text: 'text-green-200' },
        { bg: 'bg-emerald-800', text: 'text-emerald-200' },
        { bg: 'bg-purple-800', text: 'text-purple-200' },
        { bg: 'bg-violet-800', text: 'text-violet-200' },
        { bg: 'bg-red-800', text: 'text-red-200' },
        { bg: 'bg-rose-800', text: 'text-rose-200' },
        { bg: 'bg-indigo-800', text: 'text-indigo-200' },
        { bg: 'bg-teal-800', text: 'text-teal-200' },
        { bg: 'bg-cyan-800', text: 'text-cyan-200' },
        { bg: 'bg-pink-800', text: 'text-pink-200' },
        { bg: 'bg-fuchsia-800', text: 'text-fuchsia-200' },
        { bg: 'bg-yellow-800', text: 'text-yellow-200' },
        { bg: 'bg-amber-800', text: 'text-amber-200' },
        { bg: 'bg-orange-800', text: 'text-orange-200' },
        { bg: 'bg-zinc-800', text: 'text-zinc-200' },
        { bg: 'bg-neutral-800', text: 'text-neutral-200' },
        { bg: 'bg-stone-800', text: 'text-stone-200' },
        { bg: 'bg-sky-800', text: 'text-sky-200' },
        { bg: 'bg-lime-800', text: 'text-lime-200' },
    ];

    avatarColors = computed(() => {
        if (this.loading()) {
            return { bg: 'bg-gray-200', text: 'text-gray-200' };
        } else if (!this.name()) {
            return { bg: 'bg-gray-300', text: 'text-gray-300' };
        }

        const hash = Array.from(this.name()).reduce(
            (hash, char) => char.charCodeAt(0) + ((hash << 5) - hash),
            0
        );

        const index = Math.abs(hash) % this.colorPairs.length;
        return this.colorPairs[index];
    });
}
